// src/lib/api.ts — the single HTTP client. Handles the response envelope, bearer
// tokens and transparent refresh-on-401.

import type { ApiError, ListMeta, Bill, PaymentSplit, PropertyBalance, PropertyAdvance } from '../types/api';

const BASE = import.meta.env.VITE_API_URL ?? '/api/v1';

export class ApiRequestError extends Error {
  code: string;
  status: number;
  details?: unknown;
  constructor(status: number, err: ApiError) {
    super(err.message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = err.code;
    this.details = err.details;
  }
}

type Tokens = { accessToken: string; refreshToken: string };

const STORAGE_KEY = 'lotus.auth';

export const tokenStore = {
  get(): Tokens | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Tokens) : null;
    } catch {
      return null;
    }
  },
  set(tokens: Tokens) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens)); } catch { /* private mode */ }
  },
  clear() {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* private mode */ }
  },
};

let refreshing: Promise<boolean> | null = null;

/** Exchange the refresh token for a new pair. Deduplicated across concurrent 401s. */
async function refreshTokens(): Promise<boolean> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    const tokens = tokenStore.get();
    if (!tokens?.refreshToken) return false;
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken: tokens.refreshToken }),
      });
      if (!res.ok) return false;
      const body = await res.json();
      const next = body?.data;
      if (!next?.accessToken) return false;
      tokenStore.set({ accessToken: next.accessToken, refreshToken: next.refreshToken ?? tokens.refreshToken });
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Skip the Authorization header (public endpoints). */
  anonymous?: boolean;
  query?: Record<string, unknown>;
}

function buildUrl(path: string, query?: Record<string, unknown>): string {
  const url = `${BASE}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function request<T>(path: string, options: RequestOptions = {}, retry = true): Promise<{ data: T; meta?: ListMeta; extra?: Record<string, unknown> }> {
  const { body, anonymous, query, headers, ...rest } = options;
  const tokens = tokenStore.get();

  const res = await fetch(buildUrl(path, query), {
    ...rest,
    headers: {
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(!anonymous && tokens?.accessToken ? { authorization: `Bearer ${tokens.accessToken}` } : {}),
      ...(headers as Record<string, string>),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && retry && !anonymous) {
    if (await refreshTokens()) return request<T>(path, options, false);
    tokenStore.clear();
  }

  if (res.status === 204) return { data: undefined as T };

  const payload = await res.json().catch(() => null);

  if (!res.ok) {
    throw new ApiRequestError(res.status, payload?.error ?? { code: 'NETWORK', message: res.statusText });
  }
  // Anything else the body carries next to data / meta (a list's `counts`, say) rides along for useList's `extra`.
  const { data, meta, ...extra } = (payload ?? {}) as { data?: unknown; meta?: ListMeta } & Record<string, unknown>;
  return { data: data as T, meta, ...(Object.keys(extra).length ? { extra } : {}) };
}

export const api = {
  get: <T>(path: string, query?: Record<string, unknown>) => request<T>(path, { method: 'GET', query }),
  /** `headers` for the rare call that needs one, e.g. an Idempotency-Key on recording a payment. */
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) => request<T>(path, { method: 'POST', body, headers }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** Unauthenticated calls for the public website surface. */
  public: {
    get: <T>(path: string, query?: Record<string, unknown>) =>
      request<T>(path, { method: 'GET', query, anonymous: true }),
    post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body, anonymous: true }),
    delete: <T>(path: string) => request<T>(path, { method: 'DELETE', anonymous: true }),
  },
};

/**
 * A fetch with the session token and the same refresh-on-401 as `api` — for binary responses (a stored file, a zip)
 * that the JSON client cannot return. `path` is under the API base, or an absolute `/api/v1/...` URL from the server.
 */
/** The full URL for an API path (for an XHR upload, which `api` cannot report progress for). */
export const apiUrl = (path: string) => buildUrl(path);

export async function authedFetch(path: string, init: RequestInit = {}, retry = true): Promise<Response> {
  const url = /^https?:/.test(path) ? path : path.startsWith('/api/v1/') ? `${BASE}${path.slice('/api/v1'.length)}` : buildUrl(path);
  const tokens = tokenStore.get();
  const res = await fetch(url, { ...init, headers: { ...(init.headers as Record<string, string>), ...(tokens?.accessToken ? { authorization: `Bearer ${tokens.accessToken}` } : {}) } });
  if (res.status === 401 && retry && (await refreshTokens())) return authedFetch(path, init, false);
  return res;
}

/**
 * Download a list as CSV: the same endpoint and filters as the screen, plus `format=csv` (Task R). The server
 * checks `<page>.export` and returns every row in scope (up to 50,000). Fetched with the bearer token, then saved.
 */
export async function downloadCsv(path: string, query: Record<string, unknown> = {}, filename?: string, retry = true): Promise<{ rows: number; truncated: boolean }> {
  const q = { ...query, format: 'csv' } as Record<string, unknown>;
  delete q.page; delete q.perPage;
  const tokens = tokenStore.get();
  const res = await fetch(buildUrl(path, q), { headers: tokens?.accessToken ? { authorization: `Bearer ${tokens.accessToken}` } : {} });
  if (res.status === 401 && retry && (await refreshTokens())) return downloadCsv(path, query, filename, false);
  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    throw new ApiRequestError(res.status, payload?.error ?? { code: 'EXPORT_FAILED', message: res.statusText });
  }
  const blob = await res.blob();
  const fromHeader = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1];
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename ?? fromHeader ?? 'export.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
  return { rows: Number(res.headers.get('x-export-rows') ?? 0), truncated: !!res.headers.get('x-export-truncated') };
}

/**
 * Open an invoice as its printable VAT invoice (GET /invoices/:id/document, HTML with print styles — "Print → Save as
 * PDF" in the browser gives the PDF). Fetched with the session token, then shown in a new tab from a blob URL. The tab
 * is opened before the fetch so the browser treats it as the click's own pop-up.
 */
/** A draft invoice holds a DRAFT-… placeholder until it is issued and numbered; people see "Draft". */
export function invoiceNumberLabel(number: string | null | undefined): string {
  if (!number) return '';
  return number.startsWith('DRAFT-') ? 'Draft' : number;
}

export async function openInvoiceDocument(id: string): Promise<void> {
  const win = window.open('', '_blank');
  const get = () => fetch(buildUrl(`/invoices/${id}/document`), { headers: { authorization: `Bearer ${tokenStore.get()?.accessToken ?? ''}` } });
  let res = await get();
  if (res.status === 401 && (await refreshTokens())) res = await get();
  if (!res.ok) {
    win?.close();
    throw new Error(res.status === 404 ? 'That invoice is not available to you' : `Could not open the invoice (${res.status})`);
  }
  const href = URL.createObjectURL(await res.blob());
  if (win) win.location.href = href; else window.location.href = href;
  setTimeout(() => URL.revokeObjectURL(href), 60_000);
}

/** Contractor bills (AP) — the calls more than one screen makes. */
export const billsApi = {
  get: async (id: string) => (await api.get<Bill>(`/bills/${id}`)).data,
  approve: (id: string) => api.post<Bill>(`/bills/${id}/approve`, {}),
  void: (id: string, reason: string) => api.post<Bill>(`/bills/${id}/void`, { reason }),
  signOff: (id: string) => api.post<Bill>(`/bills/${id}/sign-off`, {}),
  reversePayment: (id: string, paymentId: string, reason: string) => api.post(`/bills/${id}/payments/${paymentId}/reverse`, { reason }),
  payPreview: async (id: string, body: { amountMinor?: number; useDeposits?: boolean }) => (await api.post<PaymentSplit>(`/bills/${id}/pay-preview`, body)).data,
  pay: (id: string, body: { paidOn: string; amountMinor?: number; bankAccountId?: string; useDeposits?: boolean; note?: string; method?: string; externalReference?: string | null }) =>
    api.post<{ payment: unknown; split: PaymentSplit; bill: Bill; balanceAfter: PropertyBalance }>(`/bills/${id}/pay`, body),
};

/** Property balances and the group company's advances to them. */
export const propertyFundsApi = {
  balance: async (propertyId: string) => (await api.get<PropertyBalance>(`/property-funds/${propertyId}`)).data,
  recover: (advanceId: string, body: { date: string; amountMinor?: number; note?: string }) =>
    api.post<PropertyAdvance>(`/property-funds/advances/${advanceId}/recover`, body),
};

/**
 * Open an SSE stream against a POST endpoint. EventSource cannot POST or send
 * headers, so this reads the response body directly.
 */
export async function streamSse(
  path: string,
  body: unknown,
  handlers: { onEvent: (event: string, data: unknown) => void; signal?: AbortSignal; anonymous?: boolean },
): Promise<void> {
  const tokens = tokenStore.get();
  const res = await fetch(buildUrl(path), {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(!handlers.anonymous && tokens?.accessToken ? { authorization: `Bearer ${tokens.accessToken}` } : {}),
    },
    body: JSON.stringify(body),
    signal: handlers.signal,
  });

  if (!res.ok || !res.body) {
    const payload = await res.json().catch(() => null);
    throw new ApiRequestError(res.status, payload?.error ?? { code: 'STREAM_FAILED', message: res.statusText });
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line.
    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? '';
    for (const frame of frames) {
      let event = 'message';
      const dataLines: string[] = [];
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
      }
      if (!dataLines.length) continue;
      try {
        handlers.onEvent(event, JSON.parse(dataLines.join('\n')));
      } catch {
        handlers.onEvent(event, dataLines.join('\n'));
      }
    }
  }
}
